import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext();

// Safe localStorage helpers
const storage = {
  get: (key) => {
    try {
      const val = localStorage.getItem(key);
      return val ? JSON.parse(val) : null;
    } catch { return null; }
  },
  set: (key, value) => {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn('localStorage write failed:', e);
    }
  }
};

const USER_KEY = 'decorhub_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => storage.get(USER_KEY));
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const refreshingRef = useRef(false); // prevent double refresh

  const saveUser = useCallback((data) => {
    setUser(data);
    storage.set(USER_KEY, data);
  }, []);

  const logout = useCallback(() => {
    saveUser(null);
    setUnreadCount(0);
  }, [saveUser]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await authAPI.login({ email, password });

      if (!data || !data.role) {
        return { success: false, message: 'Server se galat response aaya. Admin se contact karo.' };
      }

      saveUser(data);
      return { success: true, data };
    } catch (error) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;

      console.error('Login error full:', error);
      console.error('Status:', status);
      console.error('Server message:', serverMsg);

      if (!navigator.onLine) return { success: false, message: 'Internet nahi hai. Connection check karo.' };
      if (status === 401) return { success: false, message: 'Email ya password galat hai.' };
      if (status === 403) return { success: false, message: 'Tumhara account band kar diya gaya hai.' };
      if (status === 404) return { success: false, message: 'Ye email registered nahi hai.' };
      if (status === 429) return { success: false, message: 'Bahut zyada attempts. Thodi der baad try karo.' };
      
      // Network error — CORS ya server down
      if (error.code === 'ERR_NETWORK') return { success: false, message: 'Server se connect nahi ho pa raha. CORS issue ho sakta hai.' };
      if (error.code === 'ECONNABORTED') return { success: false, message: 'Server slow hai, timeout ho gaya. Dobara try karo.' };

      return { success: false, message: serverMsg || `Error: ${error.code} — ${error.message}` };
    } finally {
      setLoading(false);
    }
  };

  const register = async (formData) => {
    setLoading(true);
    try {
      const { data } = await authAPI.register(formData);

      if (!data || !data.role) {
        return { success: false, message: 'Server se galat response aaya. Dobara try karo.' };
      }

      saveUser(data);
      return { success: true, data };
    } catch (error) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;

      if (status === 409) return { success: false, message: 'Ye email pehle se registered hai.' };
      if (status === 422) return { success: false, message: serverMsg || 'Form data sahi nahi hai.' };
      if (!navigator.onLine) return { success: false, message: 'Internet nahi hai. Connection check karo.' };

      return { success: false, message: serverMsg || 'Registration fail hui. Dobara try karo.' };
    } finally {
      setLoading(false);
    }
  };

  const updateUserData = useCallback((updates) => {
    if (!user) return;
    const updated = { ...user, ...updates };
    saveUser(updated);
  }, [user, saveUser]);

  const refreshUser = useCallback(async () => {
    if (!user?._id) return;
    if (refreshingRef.current) return; // already refreshing, skip

    refreshingRef.current = true;
    try {
      const { data } = await authAPI.getMe();

      if (!data) return;

      const updated = {
        ...user,
        ...data.user,
        decoratorProfile: data.decoratorProfile ?? user.decoratorProfile,
      };
      saveUser(updated);
    } catch (err) {
      if (err.response?.status === 401) {
        logout(); // token expire ho gaya
      }
      // other errors silently ignore (network flicker, etc.)
    } finally {
      refreshingRef.current = false;
    }
  }, [user?._id, saveUser, logout]);

  const isAdmin = user?.role === 'admin';
  const isDecorator = user?.role === 'decorator';
  const isClient = user?.role === 'client';
  const isLoggedIn = Boolean(user);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      loading,
      isAdmin,
      isDecorator,
      isClient,
      isLoggedIn,
      updateUserData,
      refreshUser,
      unreadCount,
      setUnreadCount,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};